import { useRef, useState } from 'react'
import type { ComponentType } from 'react'
import { Pressable, Text, View } from 'react-native'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react-native'
import type { LucideProps } from 'lucide-react-native'
import { colors } from '@chillout/core'

export interface DropdownMenuItem {
  label: string
  Icon?: ComponentType<LucideProps>
  onPress: () => void
}

interface DropdownMenuProps {
  label: string
  items: DropdownMenuItem[]
  className?: string
  accessibilityLabel?: string
}

interface PanelPosition {
  top: number
  right: number
}

export function DropdownMenu({ label, items, className, accessibilityLabel }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null)
  const buttonRef = useRef<View>(null)

  function handleToggle() {
    if (!isOpen && buttonRef.current) {
      const el = buttonRef.current as unknown as HTMLElement
      const rect = el.getBoundingClientRect()
      setPanelPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    setIsOpen((v) => !v)
  }

  function handleItemPress(item: DropdownMenuItem) {
    setIsOpen(false)
    item.onPress()
  }

  const portal =
    isOpen && panelPos
      ? createPortal(
          <>
            <Pressable
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onPress={() => setIsOpen(false)}
              accessibilityLabel="Close menu"
            />
            <View
              className="fixed min-w-[180px] rounded-lg border border-border bg-canvas-card py-1.5"
              style={{
                top: panelPos.top,
                right: panelPos.right,
                zIndex: 9999,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.08,
                shadowRadius: 12,
              }}
            >
              {items.map((item) => (
                <Pressable
                  key={item.label}
                  onPress={() => handleItemPress(item)}
                  accessibilityRole="menuitem"
                  accessibilityLabel={item.label}
                  className="flex-row items-center gap-3 px-4 py-3"
                >
                  {item.Icon ? (
                    <item.Icon size={18} color={colors.text.primary} strokeWidth={1.8} />
                  ) : null}
                  <Text className="text-base font-medium text-primary">{item.label}</Text>
                </Pressable>
              ))}
            </View>
          </>,
          document.body,
        )
      : null

  return (
    <View>
      <Pressable
        ref={buttonRef}
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityState={{ expanded: isOpen }}
        className={className}
      >
        <Text className="text-base font-semibold text-inverse">{label}</Text>
        <View style={{ transform: [{ rotate: isOpen ? '180deg' : '0deg' }] }}>
          <ChevronDown size={18} color={colors.text.inverse} strokeWidth={2} />
        </View>
      </Pressable>
      {portal}
    </View>
  )
}
